/*
 * Parts finder — sidebar filters + full bracket grid.
 *
 * The section renders every bracket product server-side ([data-pf-item] with
 * data-handle / data-ship-us). This script:
 *   1. loads parts-finder-data.json (fitment rows + bracket → product handle)
 *   2. fetches any collection pages the server didn't render (>250 products)
 *   3. sorts cards by bracket number
 *   4. shows / hides cards from the vehicle + fulfillment filters, adding a
 *      "Fits: …" strip to matched cards and keeping the URL in sync
 */
if (!customElements.get('parts-finder')) {
  class PartsFinder extends HTMLElement {
    connectedCallback() {
      this.makeSelect = this.querySelector('[data-pf-make]');
      this.modelSelect = this.querySelector('[data-pf-model]');
      this.yearGroup = this.querySelector('[data-pf-year-group]');
      this.yearSelect = this.querySelector('[data-pf-year]');
      this.shipRadios = [...this.querySelectorAll('[data-pf-ship]')];
      this.clearBtn = this.querySelector('[data-pf-clear]');
      this.form = this.querySelector('[data-pf-form]');
      this.status = this.querySelector('[data-pf-status]');
      this.count = this.querySelector('[data-pf-count]');
      this.results = this.querySelector('[data-pf-results]');
      this.empty = this.querySelector('[data-pf-empty]');
      this.toggle = this.querySelector('[data-pf-toggle]');
      this.panel = this.querySelector('[data-pf-panel]');

      this.yearFilterEnabled = this.dataset.yearFilter === 'true';
      this.data = null;
      this.handleToBracket = new Map();

      this.form.addEventListener('submit', (e) => e.preventDefault());
      this.makeSelect.addEventListener('change', () => this.onMakeChange());
      this.modelSelect.addEventListener('change', () => this.onModelChange());
      this.yearSelect.addEventListener('change', () => this.applyFilters());
      this.shipRadios.forEach((r) => r.addEventListener('change', () => this.applyFilters()));
      if (this.clearBtn) this.clearBtn.addEventListener('click', () => this.clearFilters());
      this.results.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-pf-more]');
        if (btn) { e.preventDefault(); this.toggleMore(btn); }
      });
      if (this.toggle && this.panel) {
        this.toggle.addEventListener('click', () => {
          const open = !this.panel.classList.contains('is-open');
          this.panel.classList.toggle('is-open', open);
          this.toggle.setAttribute('aria-expanded', String(open));
        });
      }

      this.init();
    }

    /* ───── bootstrap ───── */
    async init() {
      this.setStatus(this.dataset.textLoading);
      await Promise.all([this.loadData(), this.loadRemainingPages()]);
      this.indexCards();
      this.sortCards();
      this.updateShipCounts();

      if (this.data) {
        const makes = [...new Set(this.data.fitments.map((f) => f.make))].sort();
        for (const make of makes) this.makeSelect.add(new Option(make, make));
        this.makeSelect.disabled = false;
      }

      this.restoreFromUrl();
      this.applyFilters();
    }

    async loadData() {
      try {
        const res = await fetch(this.dataset.url);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        this.data = await res.json();
      } catch (err) {
        console.error('[parts-finder] failed to load fitment data', err);
        this.data = null;
      }
    }

    // The section paginates at 250; if the collection is larger, pull the
    // rest through the Section Rendering API and append the cards.
    async loadRemainingPages() {
      const total = parseInt(this.dataset.total || '0', 10);
      const url = this.dataset.collectionUrl;
      const sectionFile = this.dataset.sectionFile;
      if (!url || !sectionFile) return;
      let rendered = this.items().length;
      let page = 2;
      while (rendered < total && page < 10) {
        try {
          const res = await fetch(`${url}?section_id=${sectionFile}&page=${page}`);
          if (!res.ok) break;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const extra = [...doc.querySelectorAll('[data-pf-item]')];
          if (!extra.length) break;
          extra.forEach((n) => this.results.appendChild(n));
          rendered += extra.length;
          page++;
        } catch (err) {
          console.warn('[parts-finder] extra page failed', err);
          break;
        }
      }
    }

    items() {
      return [...this.results.querySelectorAll('[data-pf-item]')];
    }

    indexCards() {
      this.handleToBracket.clear();
      if (this.data && this.data.brackets) {
        for (const [b, info] of Object.entries(this.data.brackets)) {
          if (info.product) this.handleToBracket.set(info.product, b);
        }
      }
      for (const el of this.items()) {
        const handle = el.dataset.handle;
        let b = this.handleToBracket.get(handle);
        if (!b) {
          // fall back to the number in the handle: mirror-bracket-12 → 12
          const m = /mirror-bracket-(.+)$/.exec(handle || '');
          b = m ? m[1].toUpperCase() : handle;
        }
        el.dataset.bracket = b;
      }
    }

    // Numeric sort on the bracket id (3A sorts after 3).
    sortCards() {
      const key = (el) => {
        const m = /^(\d+)([A-Za-z]*)$/.exec(el.dataset.bracket || '');
        return m ? [parseInt(m[1], 10), m[2]] : [Number.MAX_SAFE_INTEGER, el.dataset.bracket || ''];
      };
      this.items()
        .sort((a, b) => {
          const [an, as] = key(a);
          const [bn, bs] = key(b);
          return an - bn || as.localeCompare(bs);
        })
        .forEach((el) => this.results.appendChild(el));
    }

    /* ───── vehicle selects ───── */
    rowsForMake(make) {
      return this.data ? this.data.fitments.filter((f) => f.make === make) : [];
    }
    rowsForVehicle(make, model) {
      return this.rowsForMake(make).filter((f) => f.model === '*' || f.model === model);
    }

    onMakeChange() {
      const make = this.makeSelect.value;
      this.resetSelect(this.modelSelect);
      this.resetSelect(this.yearSelect);
      this.yearGroup.hidden = true;
      if (make) {
        const rows = this.rowsForMake(make);
        const models = [...new Set(rows.map((f) => f.model).filter((m) => m !== '*'))].sort();
        for (const model of models) this.modelSelect.add(new Option(model, model));
        this.modelSelect.disabled = false;
      }
      this.applyFilters();
    }

    onModelChange() {
      const make = this.makeSelect.value;
      const model = this.modelSelect.value;
      this.resetSelect(this.yearSelect);
      this.yearGroup.hidden = true;
      if (model && this.yearFilterEnabled) {
        const rows = this.rowsForVehicle(make, model);
        const bounded = rows.filter((f) => f.yf || f.yt);
        if (bounded.length > 0) {
          const now = new Date().getFullYear();
          const lo = Math.min(...bounded.map((f) => f.yf || f.yt));
          let hi = Math.max(...bounded.map((f) => f.yt || now));
          hi = Math.min(hi, now + 1);
          for (let y = hi; y >= lo; y--) this.yearSelect.add(new Option(String(y), String(y)));
          this.yearGroup.hidden = false;
        }
      }
      this.applyFilters();
    }

    resetSelect(select) {
      while (select.options.length > 1) select.remove(1);
      select.selectedIndex = 0;
      if (select === this.modelSelect) select.disabled = true;
    }

    /* ───── filtering ───── */
    currentShip() {
      const r = this.shipRadios.find((x) => x.checked);
      return r ? r.value : '';
    }

    // Returns Map<bracket, rows[]> for the vehicle filter, or null when no
    // vehicle is selected (= show everything).
    matchedBrackets() {
      const make = this.makeSelect.value;
      const model = this.modelSelect.value;
      const year = this.yearSelect.value ? parseInt(this.yearSelect.value, 10) : null;
      if (!make || !this.data) return null;
      let rows = model ? this.rowsForVehicle(make, model) : this.rowsForMake(make);
      if (year) rows = rows.filter((f) => (!f.yf || year >= f.yf) && (!f.yt || year <= f.yt));
      const by = new Map();
      for (const f of rows) {
        if (!by.has(f.b)) by.set(f.b, []);
        by.get(f.b).push(f);
      }
      return by;
    }

    applyFilters() {
      const matched = this.matchedBrackets();
      const ship = this.currentShip();
      let shown = 0;

      for (const el of this.items()) {
        const strip = el.querySelector('[data-pf-fitment]');
        const us = el.dataset.shipUs === 'true';
        let visible = true;

        if (ship === 'us' && !us) visible = false;
        if (ship === 'intl' && us) visible = false;

        if (visible && matched) {
          const rows = matched.get(el.dataset.bracket);
          if (!rows) visible = false;
          else if (strip) {
            strip.classList.remove('is-expanded');
            strip.replaceChildren(...this.fitmentNodes(rows));
          }
        } else if (strip) {
          strip.classList.remove('is-expanded');
          strip.replaceChildren();
        }

        el.hidden = !visible;
        if (visible) shown++;
      }

      const filtering = !!matched || !!ship;
      this.empty.hidden = shown > 0;
      this.setStatus(filtering ? '' : this.statusIntro());
      this.setCount(shown);
      if (this.clearBtn) this.clearBtn.hidden = !filtering;
      this.syncUrl();
    }

    // One badge per model: "Model · years/trim". The data's `note` keeps the
    // original year/trim text from the fitment sheet, so prefer it over the
    // parsed year bounds. Long lists collapse behind a "+N more" badge.
    fitmentNodes(rows) {
      const byModel = new Map();
      for (const f of rows) {
        const model = f.model === '*' ? this.dataset.textAllModels || 'All models' : f.model;
        const text = (f.note && f.note.trim()) || this.yearText(f);
        if (!byModel.has(model)) byModel.set(model, new Set());
        if (text) byModel.get(model).add(text);
      }
      const limit = parseInt(this.dataset.fitsLimit || '4', 10);
      const nodes = [];
      let i = 0;
      for (const [model, texts] of byModel) {
        const chip = document.createElement('span');
        chip.className = 'm-parts-finder__chip m-parts-finder__chip--fit';
        if (i++ >= limit) chip.hidden = true;
        const m = document.createElement('b');
        m.textContent = model;
        chip.appendChild(m);
        const years = [...texts].join(' · ');
        if (years) chip.appendChild(document.createTextNode(' ' + years));
        nodes.push(chip);
      }
      const extra = byModel.size - limit;
      if (extra > 0) {
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'm-parts-finder__chip m-parts-finder__chip--more';
        more.dataset.pfMore = '';
        more.dataset.more = (this.dataset.textMore || '+{count} more').replace('{count}', String(extra));
        more.dataset.less = this.dataset.textLess || 'Show less';
        more.textContent = more.dataset.more;
        nodes.push(more);
      }
      return nodes;
    }

    toggleMore(btn) {
      const strip = btn.closest('[data-pf-fitment]');
      const open = !strip.classList.contains('is-expanded');
      strip.classList.toggle('is-expanded', open);
      const limit = parseInt(this.dataset.fitsLimit || '4', 10);
      strip.querySelectorAll('.m-parts-finder__chip--fit').forEach((chip, idx) => {
        chip.hidden = !open && idx >= limit;
      });
      btn.textContent = open ? btn.dataset.less : btn.dataset.more;
    }

    yearText(f) {
      if (f.yf && f.yt) return f.yf === f.yt ? String(f.yf) : `${f.yf}–${f.yt}`;
      if (f.yf) return `${f.yf}+`;
      if (f.yt) return `≤${f.yt}`;
      return '';
    }

    updateShipCounts() {
      const all = this.items();
      const us = all.filter((el) => el.dataset.shipUs === 'true').length;
      const set = (k, n) => {
        const el = this.querySelector(`[data-pf-ship-count="${k}"]`);
        if (el) el.textContent = `(${n})`;
      };
      set('all', all.length);
      set('us', us);
      set('intl', all.length - us);
    }

    clearFilters() {
      this.makeSelect.value = '';
      this.resetSelect(this.modelSelect);
      this.resetSelect(this.yearSelect);
      this.yearGroup.hidden = true;
      const all = this.shipRadios.find((r) => r.value === '');
      if (all) all.checked = true;
      this.applyFilters();
    }

    /* ───── status / count ───── */
    statusIntro() {
      return this.status.getAttribute('data-intro') || '';
    }
    setStatus(text) {
      if (!this.status.hasAttribute('data-intro')) {
        this.status.setAttribute('data-intro', this.status.textContent.trim());
      }
      this.status.hidden = !text;
      this.status.textContent = text || '';
    }
    setCount(n) {
      if (!this.count) return;
      const tpl = n === 1 ? this.dataset.textCountOne : this.dataset.textCount;
      this.count.textContent = (tpl || '{count}').replace('{count}', String(n));
    }

    /* ───── URL state (make / model / year / ship) ───── */
    syncUrl() {
      const p = new URLSearchParams(window.location.search);
      const setOrDel = (k, v) => (v ? p.set(k, v) : p.delete(k));
      setOrDel('make', this.makeSelect.value);
      setOrDel('model', this.modelSelect.value);
      setOrDel('year', this.yearSelect.value);
      setOrDel('ship', this.currentShip());
      const qs = p.toString();
      history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
    }

    restoreFromUrl() {
      const p = new URLSearchParams(window.location.search);
      const ship = p.get('ship');
      const r = this.shipRadios.find((x) => x.value === ship);
      if (r) r.checked = true;

      const make = p.get('make');
      if (make && [...this.makeSelect.options].some((o) => o.value === make)) {
        this.makeSelect.value = make;
        this.onMakeChange();
        const model = p.get('model');
        if (model && [...this.modelSelect.options].some((o) => o.value === model)) {
          this.modelSelect.value = model;
          this.onModelChange();
          const year = p.get('year');
          if (year && [...this.yearSelect.options].some((o) => o.value === year)) {
            this.yearSelect.value = year;
          }
        }
      }
    }
  }
  customElements.define('parts-finder', PartsFinder);
}
